/**
 * «Юниверситет» (28.09): шторка комнаты. Закрытая — обложка в полутьме и почему закрыта;
 * открытая — сценка (только если есть scene), «Музыка комнаты» (только если есть track —
 * в макете у Шкафов/Сплита вылезал undefined) и список квестов с лучшим результатом.
 */
import { questDone, questItems, type RoomState, type UniPack, type UniProgress, type UniQuest, type UniRoom } from "@ub/shared/uni";
import { UniImg, UniVideo } from "./Media";
import { Rich } from "./rich";
import css from "./uni.module.css";

interface RoomSheetProps {
  pack: UniPack;
  room: UniRoom;
  state: RoomState;
  prog: UniProgress;
  onQuest: (q: UniQuest) => void;
  onClose: () => void;
}

export function RoomSheet({ pack, room, state, prog, onQuest, onClose }: RoomSheetProps) {
  if (state === "lock" || state === "secret") {
    const i = pack.path.indexOf(room.id);
    const prev = i > 0 ? pack.rooms.find((r) => r.id === pack.path[i - 1]) : undefined;
    const why = room.locked_text
      ?? (prev ? `Откроется, когда пройдёшь «${prev.name}».` : "Откроется, когда пройдёшь всю карту.");
    return (
      <>
        <h2 className={css.h2} data-fb-part="название комнаты">{room.name}</h2>
        <div className={css.sub} data-fb-part="подзаголовок">{room.sub}</div>
        <UniImg rel={room.cover} className={`${css.cover} ${css.dim}`} part="обложка комнаты" />
        <p className={css.why} data-fb-part="почему закрыта">{why}</p>
        <div className={css.acts}>
          <button type="button" className={`${css.btn} ${css.ghost}`} onClick={onClose}>Понятно</button>
        </div>
      </>
    );
  }

  const done = room.quests.filter((q) => questDone(prog, q.id)).length;
  return (
    <>
      <h2 className={css.h2} data-fb-part="название комнаты">{room.name}</h2>
      <div className={css.sub} data-fb-part="подзаголовок">{room.sub} · {done}/{room.quests.length} заданий</div>
      {room.scene ? (
        <div className={css.scene}>
          <UniVideo rel={room.scene} part="сценка" />
          <span className={css.tag}>СЦЕНКА</span>
        </div>
      ) : (
        <UniImg rel={room.cover} className={css.cover} part="обложка комнаты" />
      )}
      {room.track ? <div className={css.amb} data-fb-part="музыка комнаты">Музыка комнаты: <Rich text={room.track} /></div> : null}
      <div className={css.quests}>
        {room.quests.map((q) => {
          const rec = prog.quests[q.id];
          const ok = questDone(prog, q.id);
          const { title } = questItems(q, prog.cls, pack);
          const meta = [
            q.kind,
            ok ? "пройдено" : null,
            rec && rec.of ? `${ok ? "" : "лучший "}${rec.best}/${rec.of}` : null,
          ].filter(Boolean).join(" · ");
          return (
            <button
              key={q.id}
              type="button"
              className={`${css.q} ${ok ? css.qDone : ""}`}
              onClick={() => onQuest(q)}
              data-fb={`quest:${q.id}`} data-fb-name={`Квест «${title}»`}
            >
              <span className={css.ic} aria-hidden="true">{ok ? "✓" : q.boss ? "!" : "?"}</span>
              <span className={css.qMain}>
                <b>{title}</b>
                <small>{meta}</small>
                {q.note ? <small className={css.qNote} data-fb-part="пометка">{q.note}</small> : null}
              </span>
              <span className={css.rw}>+{q.xp}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}
